import { Heart } from "lucide-react";
import cswLogo from "@/assets/csw-logo-new.png";

type LinkItem = { href: string; label: string; external?: boolean };
type Column = { title: string; href?: string; links: LinkItem[] };

const COLUMNS: Column[] = [
  {
    title: "For students",
    href: "/students/",
    links: [
      { href: "/students/", label: "Students overview" },
      { href: "/study-stream/", label: "Study Stream" },
      { href: "/study-with-me/", label: "Study With Me" },
      { href: "/study-with-friends/", label: "Study With Friends" },
      { href: "/study-with-strangers/", label: "Study With Strangers" },
      { href: "/study-room/", label: "Study Room" },
      { href: "/group-study/", label: "Group Study" },
      { href: "/pomodoro-technique/", label: "Pomodoro" },
      { href: "/study-timer/", label: "Study Timer" },
      { href: "/blog/", label: "Blog" },
    ],
  },
  {
    title: "For institutions",
    href: "/institution/",
    links: [
      { href: "/institution/", label: "Institutions overview" },
      { href: "/platform/", label: "Platform" },
      { href: "/use-cases/early-warning/", label: "Early-warning signals" },
      { href: "/use-cases/retention/", label: "Retention support" },
      { href: "/use-cases/advising-workflows/", label: "Advising workflows" },
      { href: "/for/universities/", label: "Universities" },
      { href: "/for/colleges/", label: "Colleges" },
      { href: "/for/high-schools/", label: "High schools" },
      { href: "/for/research/", label: "Research & evidence" },
    ],
  },
  {
    title: "Neurodivergence",
    links: [
      { href: "/for/adhd-students/", label: "For ADHD students" },
      { href: "/for/autistic-students/", label: "For autistic students" },
      { href: "/for/trouble-focusing/", label: "Trouble focusing" },
      { href: "/for/task-initiation/", label: "Starting tasks" },
      { href: "/for/study-routines/", label: "Study routines" },
      { href: "/for/sensory-friendly-study/", label: "Sensory-friendly study" },
    ],
  },
  {
    title: "Alternatives",
    href: "/alternatives/",
    links: [
      { href: "/alternatives/", label: "All alternatives" },
      { href: "/studyverse/", label: "Studyverse alternative" },
      { href: "/online-study/focusmate/", label: "Focusmate alternative" },
    ],
  },
  {
    title: "Global community",
    links: [
      { href: "/fr/", label: "Salle d'Étude Virtuelle" },
      { href: "/jp/", label: "オンライン自習室" },
      { href: "/vn/", label: "Phòng học ảo" },
      { href: "/cn/", label: "线上自修室" },
      { href: "/tw/", label: "自修室" },
      { href: "/kr/", label: "온라인 자습실" },
    ],
  },
];

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="border-t border-border bg-background" role="contentinfo">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-10 mb-12">
          {/* Brand */}
          <article className="space-y-4 col-span-2 md:col-span-3 lg:col-span-1">
            <a href="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <img src={cswLogo} alt="CSW Logo" className="h-10 w-auto" />
              <span className="font-display text-2xl font-bold text-foreground">CSW</span>
            </a>
            <p className="text-base text-muted-foreground leading-relaxed max-w-xs">
              An online workspace built to support focus and structure across school and work — body doubling, study streams, and a global community.
            </p>
            <div className="flex items-center gap-2 text-base text-muted-foreground">
              <span>Made with</span>
              <Heart className="w-4 h-4 text-primary fill-primary" aria-hidden="true" />
              <span>for focus</span>
            </div>
          </article>

          {COLUMNS.map((col) => (
            <nav key={col.title} aria-label={col.title}>
              <h3 className="font-display font-bold text-primary text-lg mb-4 uppercase tracking-wider">
                {col.href ? (
                  <a href={col.href} className="hover:text-primary transition-colors">{col.title}</a>
                ) : (
                  col.title
                )}
              </h3>
              <ul className="space-y-2.5">
                {col.links.map((link) => (
                  <li key={link.href + link.label}>
                    <a
                      href={link.href}
                      {...(link.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                      className="text-base text-muted-foreground hover:text-primary transition-colors"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-border/50">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-muted-foreground text-base">
              © {currentYear} CSW. All rights reserved.
            </p>
            <nav className="flex items-center gap-6 text-base flex-wrap justify-center sm:justify-end" aria-label="Legal links">
              <a href="/privacy-policy/" className="text-muted-foreground hover:text-primary transition-colors">Privacy</a>
              <a href="/terms-of-use/" className="text-muted-foreground hover:text-primary transition-colors">Terms</a>
              <a href="/registration-policy/" className="text-muted-foreground hover:text-primary transition-colors">Registration</a>
              <a href="/content-monitoring-policy/" className="text-muted-foreground hover:text-primary transition-colors">Content Monitoring</a>
              <a href="/report-policy/" className="text-muted-foreground hover:text-primary transition-colors">Report</a>
            </nav>
          </div>
        </div>
      </div>
    </footer>
  );
};
